import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Edit, FileText, Shield, RotateCcw, Copyright } from 'lucide-react';
import toast from 'react-hot-toast';
import { contentPolicyService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { isSuperAdmin } from '../../utils/rolePermissions';

const ContentPolicies = () => {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  // Policy type definitions
  const policyTypes = [
    { 
      type: 'privacy_policy', 
      label: 'Privacy Policy', 
      icon: Shield,
      description: 'Manage privacy policy content'
    },
    { 
      type: 'disclaimers', 
      label: 'Disclaimers', 
      icon: FileText,
      description: 'Manage disclaimers content'
    },
    { 
      type: 'return_policy', 
      label: 'Return Policy', 
      icon: RotateCcw,
      description: 'Manage return policy content'
    },
    { 
      type: 'copyright_contents', 
      label: 'Copyright Contents', 
      icon: Copyright,
      description: 'Manage copyright contents'
    }
  ];

  // Load policies from API
  useEffect(() => {
    if (isSuperAdmin(user)) {
      loadPolicies();
    }
  }, [user]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const response = await contentPolicyService.getAll();
      const policiesData = response.data || response || [];
      
      // Create a map for easy lookup
      const policiesMap = {};
      policiesData.forEach(policy => {
        policiesMap[policy.type] = policy;
      });
      
      setPolicies(policiesMap);
    } catch (error) {
      console.error('Error loading policies:', error);
      toast.error('Failed to load content policies');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (policyType) => {
    const policy = policies[policyType] || { type: policyType, content: '' };
    setEditingPolicy(policy);
    setShowModal(true);
    reset({
      content: policy.content || ''
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPolicy(null);
    reset();
  };

  const onSubmit = async (data) => {
    if (!editingPolicy) return;

    try {
      setLoading(true);
      const policyType = editingPolicy.type;
      
      // Check if policy exists
      const existingPolicy = policies[policyType];
      
      if (existingPolicy) {
        // Update existing policy
        await contentPolicyService.update(policyType, data);
        toast.success(`${getPolicyLabel(policyType)} updated successfully!`);
      } else {
        // Create new policy
        await contentPolicyService.create({
          type: policyType,
          content: data.content
        });
        toast.success(`${getPolicyLabel(policyType)} created successfully!`);
      }
      
      closeModal();
      loadPolicies(); // Reload the list
    } catch (error) {
      console.error('Error saving policy:', error);
      toast.error('Failed to save content policy');
    } finally {
      setLoading(false);
    }
  };

  const getPolicyLabel = (type) => {
    const policyType = policyTypes.find(pt => pt.type === type);
    return policyType ? policyType.label : type;
  };

  // Check if user is Super Admin
  if (!isSuperAdmin(user)) {
    return (
      <div className="card">
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: '#6b7280'
        }}>
          <h2>Access Denied</h2>
          <p>Only Super Admin can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div style={{
          marginBottom: '24px'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '600', color: '#111827' }}>
            Content Policies Management
          </h1>
          <p style={{ color: '#6b7280', marginTop: '8px' }}>
            Manage privacy policy, disclaimers, return policy, and copyright contents. Each content type has only one record.
          </p>
        </div>

        {/* Policy Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '24px'
        }}>
          {policyTypes.map((policyType) => {
            const Icon = policyType.icon;
            const policy = policies[policyType.type];
            const hasContent = policy && policy.content && policy.content.trim().length > 0;
            
            return (
              <div
                key={policyType.type}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s',
                  cursor: 'pointer',
                  ':hover': {
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
                onClick={() => openModal(policyType.type)}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '12px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: '#eff6ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#3b82f6'
                  }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#111827',
                      margin: 0
                    }}>
                      {policyType.label}
                    </h3>
                  </div>
                </div>
                
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '8px 0',
                  lineHeight: '1.5'
                }}>
                  {policyType.description}
                </p>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <span style={{
                    fontSize: '12px',
                    color: hasContent ? '#10b981' : '#ef4444',
                    fontWeight: '500'
                  }}>
                    {hasContent ? 'Content Available' : 'No Content'}
                  </span>
                  <button
                    className="btn btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openModal(policyType.type);
                    }}
                  >
                    <Edit size={14} />
                    {hasContent ? 'Edit' : 'Add Content'}
                  </button>
                </div>

                {policy && policy.updated_at && (
                  <div style={{
                    fontSize: '11px',
                    color: '#9ca3af',
                    marginTop: '8px'
                  }}>
                    Last updated: {new Date(policy.updated_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading policies...
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && editingPolicy && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Edit {getPolicyLabel(editingPolicy.type)}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label className="form-label">Content</label>
                <textarea
                  className="form-input"
                  placeholder="Enter content here..."
                  rows={15}
                  style={{
                    minHeight: '400px',
                    fontFamily: 'monospace',
                    resize: 'vertical'
                  }}
                  {...register('content', {
                    required: 'Content is required'
                  })}
                />
                {errors.content && (
                  <div className="error-message">{errors.content.message}</div>
                )}
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '8px'
                }}>
                  You can use HTML formatting in the content field.
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Content'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentPolicies;

